import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { Award, BarChart3, Brain, CheckCircle, ChevronRight, ClipboardList, Crown, Eye, FileText, Gift, Home, Medal, Phone, RefreshCw, Search, Shield, Sparkles, Star, Trophy, User, UserCheck } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalRecognition = () => {
  const navigate = useNavigate();
  const [observers, setObservers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedObserver, setSelectedObserver] = useState(null);
  const [recognition, setRecognition] = useState(null);
  const [loadingRecognition, setLoadingRecognition] = useState(false);
  const [awarding, setAwarding] = useState(false);
  const [customAward, setCustomAward] = useState({ title: '', description: '' });

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadObservers();
  }, [navigate]);

  const loadObservers = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(`${BACKEND_URL}/api/principal/observers`, {
        params: { token }
      });
      setObservers(response.data.observers || []);
    } catch (error) {
      console.error('Error loading observers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecognition = async (observerId) => {
    setLoadingRecognition(true);
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(`${BACKEND_URL}/api/principal/observer/${observerId}/recognition`, {
        params: { token }
      });
      setRecognition(response.data);
    } catch (error) {
      console.error('Error loading recognition:', error);
      setRecognition({ certifications: [], achievements: [], next_milestone: null, stats: {} });
    } finally {
      setLoadingRecognition(false);
    }
  };

  const selectObserver = (observer) => {
    setSelectedObserver(observer);
    loadRecognition(observer.id);
  };

  const awardAchievement = async () => {
    if (!customAward.title.trim()) {
      alert('Please enter an award title');
      return;
    }
    
    setAwarding(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/observer/${selectedObserver.id}/award`, null, {
        params: { 
          token,
          title: customAward.title,
          description: customAward.description
        }
      });
      
      setCustomAward({ title: '', description: '' });
      await loadRecognition(selectedObserver.id);
      alert('Achievement awarded successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to award achievement');
    } finally {
      setAwarding(false);
    }
  };

  const getCertIcon = (certId) => {
    switch (certId) {
      case 'master': return Crown;
      case 'gold': return Medal;
      case 'silver': return Star;
      default: return Award;
    }
  };

  const getCertColor = (certId) => {
    switch (certId) {
      case 'master': return '#9333EA';
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      default: return '#CD7F32';
    }
  };

  const filteredObservers = observers.filter(obs =>
    obs.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="principal-recognition-page">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Trophy className="w-7 h-7 text-yellow-500" />
                Recognition & Certification
              </h1>
              <p className="text-slate-600">View and award observer achievements</p>
            </div>
            <Button variant="outline" onClick={loadObservers} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Observer List */}
            <Card className="lg:col-span-1">
              <CardContent className="p-4">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search observers..."
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredObservers.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No observers found</p>
                  ) : (
                    filteredObservers.map(obs => (
                      <button
                        key={obs.id}
                        onClick={() => selectObserver(obs)}
                        className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                          selectedObserver?.id === obs.id 
                            ? 'bg-blue-50 border-2 border-sanjaya-navy' 
                            : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                        }`}
                        data-testid={`observer-item-${obs.id}`}
                      >
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-sanjaya-navy" />
                        </div>
                        <div className="text-left flex-1">
                          <p className="font-medium text-slate-800">{obs.name}</p>
                          <p className="text-xs text-slate-500">{obs.assigned_children || 0} children</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recognition Details */}
            <Card className="lg:col-span-2">
              <CardContent className="p-6">
                {!selectedObserver ? (
                  <div className="text-center py-16">
                    <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Select an observer to view their recognition</p>
                  </div>
                ) : loadingRecognition ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy mx-auto"></div>
                  </div>
                ) : (
                  <div>
                    {/* Observer Header */}
                    <div className="flex items-center gap-4 mb-6 pb-4 border-b">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                        {selectedObserver.name?.charAt(0)}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-slate-800">{selectedObserver.name}</h2>
                        <p className="text-slate-500">{selectedObserver.email}</p>
                        <p className="text-sm text-sanjaya-navy">{recognition?.stats?.total_sessions || 0} sessions completed</p>
                      </div>
                    </div>

                    {/* Certifications */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <Medal className="w-5 h-5 text-yellow-500" />
                        Certifications
                      </h3>
                      
                      {recognition?.certifications?.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                          {recognition.certifications.map(cert => {
                            const Icon = getCertIcon(cert.id);
                            const color = getCertColor(cert.id);
                            return (
                              <div 
                                key={cert.id}
                                className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border"
                              >
                                <div 
                                  className="w-12 h-12 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: `${color}20` }}
                                >
                                  <Icon className="w-6 h-6" style={{ color }} />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800">{cert.name}</p>
                                  <p className="text-xs text-green-600 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" /> Earned
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-slate-50 rounded-lg p-6 text-center">
                          <Star className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500">No certifications earned yet</p>
                          <p className="text-xs text-slate-400 mt-1">Complete 10+ sessions to earn first certification</p>
                        </div>
                      )}
                    </div>

                    {/* Next Milestone */}
                    {recognition?.next_milestone && (
                      <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-orange-500" />
                          Next Milestone
                        </h3>
                        <p className="text-slate-700">{recognition.next_milestone}</p>
                      </div>
                    )}

                    {/* Custom Achievements */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <Gift className="w-5 h-5 text-purple-500" />
                        Achievements
                      </h3>
                      
                      {recognition?.achievements?.length > 0 ? (
                        <div className="space-y-2 mb-4">
                          {recognition.achievements.map((ach, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                              <Trophy className="w-5 h-5 text-purple-500" />
                              <div>
                                <p className="font-medium text-slate-800">{ach.title}</p>
                                {ach.description && <p className="text-sm text-slate-500">{ach.description}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm mb-4">No custom achievements awarded yet</p>
                      )}
                    </div>

                    {/* Award Custom Achievement */}
                    <div className="bg-slate-50 rounded-lg p-4 border">
                      <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                        <Gift className="w-4 h-4 text-blue-500" />
                        Award Custom Achievement
                      </h4>
                      <div className="space-y-3">
                        <Input
                          value={customAward.title}
                          onChange={(e) => setCustomAward({ ...customAward, title: e.target.value })}
                          placeholder="Achievement title (e.g., 'Star Performer')"
                          data-testid="award-title-input"
                        />
                        <Input
                          value={customAward.description}
                          onChange={(e) => setCustomAward({ ...customAward, description: e.target.value })}
                          placeholder="Description (optional)"
                          data-testid="award-description-input"
                        />
                        <Button 
                          onClick={awardAchievement}
                          disabled={awarding || !customAward.title.trim()}
                          className="w-full bg-purple-600 hover:bg-purple-700"
                          data-testid="award-btn"
                        >
                          {awarding ? 'Awarding...' : (
                            <>
                              <Trophy className="w-4 h-4 mr-2" />
                              Award Achievement
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrincipalRecognition;
